import { getErrorMessage } from '@/lib/functions';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { genderedArticle } from '@/lib/format';
import { playAlert } from '@/lib/sounds';
import { ZoomableImageModal } from '@/components/ZoomableImageModal';
import {
  DISPUTE_TAGS,
  DisputeInfo,
  acceptDispute,
  captureAndUploadDisputeEvidence,
  contestDispute,
  declareCommunication,
  getDisputeEvidenceSignedUrl,
  openDispute,
  persistDispute,
  pickAndUploadDisputeEvidence,
  proposeMutualResolution,
  respondMutualResolution,
  withdrawDispute,
  disputeTagLabel,
} from '@/lib/disputes';
import { ErrorText } from '@/components/ErrorText';

/** Θάμπναϊλ αποδεικτικού με signed URL, φορτώνεται lazy ανά path. */
function EvidenceThumb({ path, onPress, onRemove, colors, styles }: {
  path: string;
  onPress: (uri: string) => void;
  onRemove?: () => void;
  colors: AppColors;
  styles: ReturnType<typeof makeStyles>;
}) {
  const [uri, setUri] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void getDisputeEvidenceSignedUrl(path).then((u) => { if (alive) setUri(u); }).catch(() => {});
    return () => { alive = false; };
  }, [path]);

  return (
    <View style={styles.evidenceThumbWrap}>
      <Pressable onPress={() => uri && onPress(uri)} accessibilityRole="button">
        {uri ? (
          <Image source={{ uri }} style={styles.evidenceThumb} />
        ) : (
          <View style={[styles.evidenceThumb, styles.evidenceThumbLoading]}>
            <ActivityIndicator size="small" color={colors.textMuted} />
          </View>
        )}
      </Pressable>
      {!!onRemove && (
        <Pressable style={styles.evidenceRemoveBtn} onPress={onRemove} hitSlop={8} accessibilityRole="button">
          <Ionicons name="close" size={13} color="#fff" />
        </Pressable>
      )}
    </View>
  );
}

/** Επισύναψη αποδεικτικών (screenshots από το chat της συμφωνίας) — reusable,
 * αυτόνομο block για οποιαδήποτε φόρμα διαφωνίας (2-ατόμων ή πολλαπλών
 * μερών). Ελεγχόμενο από τον γονέα μέσω evidence/onChange, ώστε ο γονέας να
 * κρατάει το state και να το περνάει στο σχετικό RPC μαζί με tags/statement. */
export function EvidenceAttachSection({ agreementId, evidence, onChange }: {
  agreementId: string;
  evidence: string[];
  onChange: (next: string[]) => void;
}) {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [uploading, setUploading] = useState(false);
  const [viewerUri, setViewerUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onAdd() {
    if (!user) return;
    setUploading(true); setError(null);
    try {
      const path = await pickAndUploadDisputeEvidence(user.id);
      if (path) onChange([...evidence, path]);
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setUploading(false);
    }
  }

  async function onCapture() {
    if (!user) return;
    setUploading(true); setError(null);
    try {
      const path = await captureAndUploadDisputeEvidence(user.id);
      if (path) onChange([...evidence, path]);
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <Text style={styles.evidenceLabel}>{t('dispute.evidence_title')}</Text>
      <Pressable
        style={styles.btnSecondary}
        onPress={() => router.push({ pathname: '/(app)/agreements/messages/[id]', params: { id: agreementId } } as any)}
        accessibilityRole="button"
      >
        <Text style={styles.btnSecondaryText}>{t('dispute.evidence_openChat')}</Text>
      </Pressable>
      <View style={styles.btnRow}>
        <Pressable
          style={[styles.btnSecondary, uploading && styles.btnDisabled]}
          onPress={onCapture}
          disabled={uploading}
          accessibilityRole="button"
        >
          <Text style={styles.btnSecondaryText}>{t('dispute.evidence_takePhoto')}</Text>
        </Pressable>
        <Pressable
          style={[styles.btnPrimary, uploading && styles.btnDisabled]}
          onPress={onAdd}
          disabled={uploading}
          accessibilityRole="button"
        >
          {uploading
            ? <ActivityIndicator color="#fff" size="small" />
            : <Text style={styles.btnPrimaryText}>{t('dispute.evidence_addScreenshot')}</Text>
          }
        </Pressable>
      </View>
      {evidence.length > 0 && (
        <View style={styles.evidenceGrid}>
          {evidence.map((path) => (
            <EvidenceThumb
              key={path}
              path={path}
              onPress={setViewerUri}
              onRemove={() => onChange(evidence.filter((x) => x !== path))}
              colors={colors}
              styles={styles}
            />
          ))}
        </View>
      )}
      <ErrorText>{error}</ErrorText>
      <ZoomableImageModal uri={viewerUri} onClose={() => setViewerUri(null)} />
    </>
  );
}

// ── Ανοιγμα νέας διαφωνίας (από τον Α) ──────────────────────────────────────

type OpenProps = {
  agreementId: string;
  onDone: () => void;
  onCancel: () => void;
};

export function OpenDisputeFlow({ agreementId, onDone, onCancel }: OpenProps) {
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const router = useRouter();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [wizardStep, setWizardStep] = useState(1);
  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState<string[]>([]);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [viewerUri, setViewerUri] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(8);

  useEffect(() => {
    if (wizardStep !== 4) return;
    setCountdown(8);
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [wizardStep]);

  function toggleTag(key: string) {
    setTags((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
  }

  async function onAddEvidence() {
    if (!user) return;
    setUploadingEvidence(true); setError(null);
    try {
      const path = await pickAndUploadDisputeEvidence(user.id);
      if (path) setEvidence((p) => [...p, path]);
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setUploadingEvidence(false);
    }
  }

  async function onCaptureEvidence() {
    if (!user) return;
    setUploadingEvidence(true); setError(null);
    try {
      const path = await captureAndUploadDisputeEvidence(user.id);
      if (path) setEvidence((p) => [...p, path]);
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setUploadingEvidence(false);
    }
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      await openDispute(agreementId, tags, note, evidence);
      playAlert();
      onDone();
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setBusy(false);
    }
  }

  const TOTAL = 4;

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={[styles.overlay, { justifyContent: 'flex-end' }]}>
        <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]}>
          <View style={styles.handle} />

          {/* Wizard progress */}
          <View style={styles.wizardHeader}>
            <Text style={styles.wizardStep}>{t('common.stepOf', { step: wizardStep, total: TOTAL })}</Text>
            <View style={styles.wizardTrack}>
              <View style={[styles.wizardFill, { width: `${(wizardStep / TOTAL) * 100}%` as any }]} />
            </View>
          </View>

          {/* ── Βήμα 1: Αιτία ── */}
          {wizardStep === 1 && (
            <>
              <Text style={styles.title}>{t('dispute.open_step1_title')}</Text>
              <Text style={styles.body}>{t('dispute.open_step1_body')}</Text>
              <View style={styles.tagGrid}>
                {DISPUTE_TAGS.map((t) => {
                  const sel = tags.includes(t.key);
                  return (
                    <Pressable key={t.key} style={[styles.tag, sel && styles.tagSelected]} onPress={() => toggleTag(t.key)}>
                      <Text style={[styles.tagText, sel && styles.tagTextSelected]}>{t.label}</Text>
                    </Pressable>
                  );
                })}
              </View>
              <ErrorText>{error}</ErrorText>
              <View style={styles.btnRow}>
                <Pressable style={styles.btnSecondary} onPress={onCancel}>
                  <Text style={styles.btnSecondaryText}>{t('common.cancel')}</Text>
                </Pressable>
                <Pressable
                  style={[styles.btnPrimary, tags.length === 0 && styles.btnDisabled]}
                  onPress={() => { if (tags.length === 0) { setError(t('errors.selectAtLeastOneTag')); return; } setError(null); setWizardStep(2); }}
                  disabled={tags.length === 0}
                >
                  <Text style={styles.btnPrimaryText}>{t('common.next')}</Text>
                </Pressable>
              </View>
            </>
          )}

          {/* ── Βήμα 2: Επεξήγηση ── */}
          {wizardStep === 2 && (
            <>
              <Text style={styles.title}>{t('dispute.open_step2_title')}</Text>
              <Text style={styles.body}>{t('dispute.open_step2_body')}</Text>
              <TextInput
                value={note}
                onChangeText={setNote}
                placeholder={t('dispute.open_step2_placeholder')}
                placeholderTextColor={colors.textMuted}
                multiline
                numberOfLines={4}
                style={[styles.noteInput, { color: colors.text, backgroundColor: colors.surface, borderColor: colors.border }]}
              />
              <View style={styles.btnRow}>
                <Pressable style={styles.btnSecondary} onPress={() => setWizardStep(1)}>
                  <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                </Pressable>
                <Pressable style={styles.btnPrimary} onPress={() => setWizardStep(3)}>
                  <Text style={styles.btnPrimaryText}>{t('common.next')}</Text>
                </Pressable>
              </View>
            </>
          )}

          {/* ── Βήμα 3: Αποδεικτικά (screenshots από το chat της συμφωνίας) ── */}
          {wizardStep === 3 && (
            <>
              <Text style={styles.title}>{t('dispute.evidence_title')}</Text>
              <Text style={styles.body}>{t('dispute.evidence_body')}</Text>
              <Pressable
                style={styles.btnSecondary}
                onPress={() => router.push({ pathname: '/(app)/agreements/messages/[id]', params: { id: agreementId } } as any)}
                accessibilityRole="button"
              >
                <Text style={styles.btnSecondaryText}>{t('dispute.evidence_openChat')}</Text>
              </Pressable>
              <View style={styles.btnRow}>
                <Pressable
                  style={[styles.btnSecondary, uploadingEvidence && styles.btnDisabled]}
                  onPress={onCaptureEvidence}
                  disabled={uploadingEvidence}
                  accessibilityRole="button"
                >
                  <Text style={styles.btnSecondaryText}>{t('dispute.evidence_takePhoto')}</Text>
                </Pressable>
                <Pressable
                  style={[styles.btnPrimary, uploadingEvidence && styles.btnDisabled]}
                  onPress={onAddEvidence}
                  disabled={uploadingEvidence}
                  accessibilityRole="button"
                >
                  {uploadingEvidence
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={styles.btnPrimaryText}>{t('dispute.evidence_addScreenshot')}</Text>
                  }
                </Pressable>
              </View>
              {evidence.length > 0 && (
                <View style={styles.evidenceGrid}>
                  {evidence.map((path) => (
                    <EvidenceThumb
                      key={path}
                      path={path}
                      onPress={setViewerUri}
                      onRemove={() => setEvidence((p) => p.filter((x) => x !== path))}
                      colors={colors}
                      styles={styles}
                    />
                  ))}
                </View>
              )}
              <ErrorText>{error}</ErrorText>
              <View style={styles.btnRow}>
                <Pressable style={styles.btnSecondary} onPress={() => setWizardStep(2)}>
                  <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                </Pressable>
                <Pressable style={styles.btnPrimary} onPress={() => setWizardStep(4)}>
                  <Text style={styles.btnPrimaryText}>{t('common.next')}</Text>
                </Pressable>
              </View>
              <ZoomableImageModal uri={viewerUri} onClose={() => setViewerUri(null)} />
            </>
          )}

          {/* ── Βήμα 4: Επιβεβαίωση & Συνέπειες ── */}
          {wizardStep === 4 && (
            <>
              <View style={styles.iconRow}>
                <Ionicons name="alert-circle" size={32} color={colors.warning} />
              </View>
              <Text style={styles.title}>{t('dispute.open_step3_title')}</Text>
              <Text style={styles.body}>{t('dispute.open_step3_body')}</Text>
              <View style={styles.bullets}>
                <Bullet colors={colors} styles={styles} icon="wallet-outline" text={t('dispute.open_step3_bullet_stake')} color={colors.warning} />
                <Bullet colors={colors} styles={styles} icon="snow-outline" text={t('dispute.open_step3_bullet_freeze')} />
                <Bullet colors={colors} styles={styles} icon="time-outline" text={t('dispute.open_step3_bullet_deadline')} color={colors.warning} />
                <Bullet colors={colors} styles={styles} icon="checkmark-circle-outline" text={t('dispute.open_step3_bullet_accept')} color={colors.warning} />
                <Bullet colors={colors} styles={styles} icon="close-circle-outline" text={t('dispute.open_step3_bullet_contest')} color={colors.danger} />
                <Bullet colors={colors} styles={styles} icon="arrow-undo-outline" text={t('dispute.open_step3_bullet_withdraw')} />
              </View>
              <ErrorText>{error}</ErrorText>
              <View style={styles.btnRow}>
                <Pressable style={styles.btnSecondary} onPress={() => setWizardStep(3)}>
                  <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                </Pressable>
                <Pressable
                  style={[styles.btnDanger, (busy || countdown > 0) && styles.btnDisabled]}
                  onPress={submit}
                  disabled={busy || countdown > 0}
                >
                  {busy
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={styles.btnPrimaryText}>{countdown > 0 ? t('dispute.submitCountdown', { count: countdown }) : t('dispute.submitDispute')}</Text>
                  }
                </Pressable>
              </View>
            </>
          )}
        </View>
      </View>
      </KeyboardAvoidingView>
    </View>
  );
}

// ── Απάντηση από τον Β ────────────────────────────────────────────────────────

type RespondProps = {
  dispute: DisputeInfo;
  onDone: () => void;
  onCancel: () => void;
};

export function RespondDisputeFlow({ dispute, onDone, onCancel }: RespondProps) {
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const router = useRouter();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [step, setStep] = useState<'choose' | 'accept_confirm' | 'contest_tags'>('choose');
  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState<string[]>([]);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [viewerUri, setViewerUri] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [commBusy, setCommBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleTag(key: string) {
    setTags((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
  }

  async function onAddEvidence() {
    if (!user) return;
    setUploadingEvidence(true); setError(null);
    try {
      const path = await pickAndUploadDisputeEvidence(user.id);
      if (path) setEvidence((p) => [...p, path]);
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setUploadingEvidence(false);
    }
  }

  async function onCaptureEvidence() {
    if (!user) return;
    setUploadingEvidence(true); setError(null);
    try {
      const path = await captureAndUploadDisputeEvidence(user.id);
      if (path) setEvidence((p) => [...p, path]);
    } catch (e) {
      setError(getErrorMessage(e, t('dispute.errorGeneric')));
    } finally {
      setUploadingEvidence(false);
    }
  }

  async function doAccept() {
    setBusy(true); setError(null);
    try { await acceptDispute(dispute.id); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setBusy(false); }
  }

  async function doContest() {
    if (tags.length === 0) { setError(t('errors.selectAtLeastOneTag')); return; }
    setBusy(true); setError(null);
    try { await contestDispute(dispute.id, tags, note, evidence); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setBusy(false); }
  }

  async function doDeclareComm() {
    setCommBusy(true); setError(null);
    try { await declareCommunication(dispute.id); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setCommBusy(false); }
  }

  const expiresDate = new Date(dispute.expires_at).toLocaleDateString('el-GR');

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={[styles.overlay, { justifyContent: 'flex-end' }]}>
        <ScrollView>
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]}>
            <View style={styles.handle} />

            {step === 'choose' && (
              <>
                <View style={styles.iconRow}>
                  <Ionicons name="alert-circle" size={32} color={colors.warning} />
                </View>
                <Text style={styles.title}>{t('dispute.respond_title')}</Text>
                <Text style={styles.body}>
                  {t('dispute.respond_body', { date: expiresDate })}
                </Text>
                <Text style={styles.noteLabel}>{t('dispute.respond_note_label')}</Text>
                <Text style={styles.noteText}>{t('dispute.respond_note_body')}</Text>
                <ErrorText>{error}</ErrorText>
                {!dispute.communication_declared_at && (
                  <Pressable
                    style={[styles.btnCommunication, commBusy && styles.btnDisabled]}
                    onPress={doDeclareComm}
                    disabled={commBusy}
                  >
                    {commBusy
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('dispute.action_commDeclare')}</Text>
                    }
                  </Pressable>
                )}
                {!!dispute.communication_declared_at && (
                  <View style={styles.commDeclaredBadge}>
                    <Ionicons name="checkmark-circle" size={15} color="#10B981" />
                    <Text style={styles.commDeclaredText}>{t('dispute.commDeclared')}</Text>
                  </View>
                )}
                <Text style={styles.btnHint}>{t('dispute.inAppMessagesNote')}</Text>
                <Pressable style={styles.btnWarning} onPress={() => setStep('accept_confirm')}>
                  <Text style={styles.btnPrimaryText}>{t('dispute.action_acceptDispute')}</Text>
                </Pressable>
                <Text style={styles.btnHint}>{t('dispute.respond_hintAccept')}</Text>
                <Pressable style={styles.btnDanger} onPress={() => setStep('contest_tags')}>
                  <Text style={styles.btnPrimaryText}>{t('dispute.action_contestDispute')}</Text>
                </Pressable>
                <Text style={styles.btnHint}>{t('dispute.respond_hintContest')}</Text>
                <Pressable style={styles.btnSecondary} onPress={onCancel}>
                  <Text style={styles.btnSecondaryText}>{t('dispute.action_decideLater')}</Text>
                </Pressable>
              </>
            )}

            {step === 'accept_confirm' && (
              <>
                <Text style={styles.title}>{t('dispute.accept_title')}</Text>
                <Text style={styles.body}>
                  {t('dispute.accept_body')}
                </Text>
                <View style={styles.bullets}>
                  <Bullet colors={colors} styles={styles} icon="star-half-outline" text={t('dispute.accept_bullet_rating')} color={colors.warning} />
                  <Bullet colors={colors} styles={styles} icon="checkmark-circle-outline" text={t('dispute.accept_bullet_cancel')} />
                </View>
                <ErrorText>{error}</ErrorText>
                <View style={styles.btnRow}>
                  <Pressable style={styles.btnSecondary} onPress={() => setStep('choose')}>
                    <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.btnDanger, busy && styles.btnDisabled]}
                    onPress={doAccept}
                    disabled={busy}
                  >
                    {busy
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('dispute.action_acceptPenalty')}</Text>
                    }
                  </Pressable>
                </View>
              </>
            )}

            {step === 'contest_tags' && (
              <>
                <Text style={styles.title}>{t('dispute.myStatement_title')}</Text>
                <Text style={styles.body}>{t('dispute.myStatement_body')}</Text>
                <View style={styles.tagGrid}>
                  {DISPUTE_TAGS.map((t) => {
                    const sel = tags.includes(t.key);
                    return (
                      <Pressable
                        key={t.key}
                        style={[styles.tag, sel && styles.tagSelected]}
                        onPress={() => toggleTag(t.key)}
                      >
                        <Text style={[styles.tagText, sel && styles.tagTextSelected]}>{t.label}</Text>
                      </Pressable>
                    );
                  })}
                </View>
                <Text style={styles.noteLabel}>{t('dispute.open_step2_title')}</Text>
                <TextInput
                  value={note}
                  onChangeText={setNote}
                  placeholder={t('dispute.open_step2_placeholder')}
                  placeholderTextColor={colors.textMuted}
                  multiline
                  numberOfLines={4}
                  style={[styles.noteInput, { color: colors.text, backgroundColor: colors.surface, borderColor: colors.border }]}
                />

                <Text style={styles.noteLabel}>{t('dispute.evidence_title')}</Text>
                <Text style={styles.noteText}>{t('dispute.evidence_body')}</Text>
                <Pressable
                  style={styles.btnSecondary}
                  onPress={() => router.push({ pathname: '/(app)/agreements/messages/[id]', params: { id: dispute.agreement_id } } as any)}
                  accessibilityRole="button"
                >
                  <Text style={styles.btnSecondaryText}>{t('dispute.evidence_openChat')}</Text>
                </Pressable>
                <View style={styles.btnRow}>
                  <Pressable
                    style={[styles.btnSecondary, uploadingEvidence && styles.btnDisabled]}
                    onPress={onCaptureEvidence}
                    disabled={uploadingEvidence}
                    accessibilityRole="button"
                  >
                    <Text style={styles.btnSecondaryText}>{t('dispute.evidence_takePhoto')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.btnPrimary, uploadingEvidence && styles.btnDisabled]}
                    onPress={onAddEvidence}
                    disabled={uploadingEvidence}
                    accessibilityRole="button"
                  >
                    {uploadingEvidence
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('dispute.evidence_addScreenshot')}</Text>
                    }
                  </Pressable>
                </View>
                {evidence.length > 0 && (
                  <View style={styles.evidenceGrid}>
                    {evidence.map((path) => (
                      <EvidenceThumb
                        key={path}
                        path={path}
                        onPress={setViewerUri}
                        onRemove={() => setEvidence((p) => p.filter((x) => x !== path))}
                        colors={colors}
                        styles={styles}
                      />
                    ))}
                  </View>
                )}

                <View style={styles.bullets}>
                  <Bullet colors={colors} styles={styles} icon="eye-outline" text={t('dispute.myStatement_bullet_reveal')} />
                  <Bullet colors={colors} styles={styles} icon="close-circle-outline" text={t('dispute.myStatement_bullet_final')} color={colors.danger} />
                  <Bullet colors={colors} styles={styles} icon="star-half-outline" text={t('dispute.myStatement_bullet_stars')} color={colors.danger} />
                </View>
                <ErrorText>{error}</ErrorText>
                <ZoomableImageModal uri={viewerUri} onClose={() => setViewerUri(null)} />
                <View style={styles.btnRow}>
                  <Pressable style={styles.btnSecondary} onPress={() => setStep('choose')}>
                    <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.btnDanger, busy && styles.btnDisabled]}
                    onPress={doContest}
                    disabled={busy}
                  >
                    {busy
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('dispute.action_submitStatement')}</Text>
                    }
                  </Pressable>
                </View>
              </>
            )}
          </View>
        </ScrollView>
      </View>
      </KeyboardAvoidingView>
    </View>
  );
}

// ── Αποκάλυψη & επιλογή του Α μετά από αμοιβαία υποβολή ─────────────────────

type ResolveProps = {
  dispute: DisputeInfo;
  otherName: string;
  onDone: () => void;
  onCancel: () => void;
};

export function ResolveDisputeFlow({ dispute, otherName, onDone, onCancel }: ResolveProps) {
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [step, setStep] = useState<'reveal' | 'withdraw_confirm' | 'mutual_confirm' | 'persist_confirm'>('reveal');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerUri, setViewerUri] = useState<string | null>(null);

  async function doWithdraw() {
    setBusy(true); setError(null);
    try { await withdrawDispute(dispute.id); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setBusy(false); }
  }

  async function doProposeMutual() {
    setBusy(true); setError(null);
    try { await proposeMutualResolution(dispute.id); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setBusy(false); }
  }

  async function doPersist() {
    setBusy(true); setError(null);
    try { await persistDispute(dispute.id); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setBusy(false); }
  }

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={[styles.overlay, { justifyContent: 'flex-end' }]}>
        <ScrollView>
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]}>
            <View style={styles.handle} />

            {step === 'reveal' && (
              <>
                <View style={styles.iconRow}>
                  <Ionicons name="eye-outline" size={32} color={colors.primary} />
                </View>
                <Text style={styles.title}>{t('dispute.reveal_title')}</Text>
                <Text style={styles.body}>{t('dispute.reveal_body')}</Text>

                <View style={styles.revealBlock}>
                  <Text style={styles.revealWho}>{t('dispute.reveal_myReasons')}</Text>
                  <View style={styles.tagGrid}>
                    {(dispute.my_tags ?? []).map((k) => (
                      <View key={k} style={styles.tagReadOnly}>
                        <Text style={styles.tagReadOnlyText}>{disputeTagLabel(k)}</Text>
                      </View>
                    ))}
                  </View>
                  {!!dispute.my_statement && <Text style={styles.body}>{dispute.my_statement}</Text>}
                  {dispute.my_evidence_urls.length > 0 && (
                    <View style={styles.evidenceGrid}>
                      {dispute.my_evidence_urls.map((path) => (
                        <EvidenceThumb key={path} path={path} onPress={setViewerUri} colors={colors} styles={styles} />
                      ))}
                    </View>
                  )}
                </View>
                <View style={styles.revealBlock}>
                  <Text style={styles.revealWho}>{t('dispute.reveal_otherReasons', { name: otherName })}</Text>
                  <View style={styles.tagGrid}>
                    {(dispute.other_tags ?? []).map((k) => (
                      <View key={k} style={[styles.tagReadOnly, styles.tagReadOnlyOther]}>
                        <Text style={styles.tagReadOnlyText}>{disputeTagLabel(k)}</Text>
                      </View>
                    ))}
                  </View>
                  {!!dispute.other_statement && <Text style={styles.body}>{dispute.other_statement}</Text>}
                  {!!dispute.other_evidence_urls && dispute.other_evidence_urls.length > 0 && (
                    <View style={styles.evidenceGrid}>
                      {dispute.other_evidence_urls.map((path) => (
                        <EvidenceThumb key={path} path={path} onPress={setViewerUri} colors={colors} styles={styles} />
                      ))}
                    </View>
                  )}
                </View>
                <ZoomableImageModal uri={viewerUri} onClose={() => setViewerUri(null)} />

                <Text style={styles.body}>{t('dispute.reveal_whatToDo')}</Text>
                <Pressable style={styles.btnPrimary} onPress={() => setStep('mutual_confirm')}>
                  <Text style={styles.btnPrimaryText}>{t('dispute.action_sendProposal')}</Text>
                </Pressable>
                <Pressable style={styles.btnSecondary} onPress={() => setStep('withdraw_confirm')}>
                  <Text style={styles.btnSecondaryText}>{t('dispute.action_withdrawPenaltySmall')}</Text>
                </Pressable>
                <Pressable style={styles.btnDanger} onPress={() => setStep('persist_confirm')}>
                  <Text style={styles.btnPrimaryText}>{t('dispute.action_persistPenalty')}</Text>
                </Pressable>
                <Pressable style={[styles.btnSecondary, { opacity: 0.6 }]} onPress={onCancel}>
                  <Text style={styles.btnSecondaryText}>{t('dispute.action_decideLater')}</Text>
                </Pressable>
              </>
            )}

            {step === 'mutual_confirm' && (
              <>
                <Text style={styles.title}>{t('dispute.respondMutual_title')}</Text>
                <View style={styles.bullets}>
                  <Bullet colors={colors} styles={styles} icon="people-outline" text={t('dispute.mutual_proposal', { name: otherName })} />
                  <Bullet colors={colors} styles={styles} icon="time-outline" text={t('dispute.mutual_deadline')} />
                  <Bullet colors={colors} styles={styles} icon="checkmark-circle-outline" text={t('dispute.mutual_accept')} color={colors.success} />
                  <Bullet colors={colors} styles={styles} icon="close-circle-outline" text={t('dispute.mutual_reject')} />
                </View>
                <ErrorText>{error}</ErrorText>
                <View style={styles.btnRow}>
                  <Pressable style={styles.btnSecondary} onPress={() => { setError(null); setStep('reveal'); }}>
                    <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.btnPrimary, busy && styles.btnDisabled]}
                    onPress={doProposeMutual}
                    disabled={busy}
                  >
                    {busy
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('dispute.action_sendProposal')}</Text>
                    }
                  </Pressable>
                </View>
              </>
            )}

            {step === 'withdraw_confirm' && (
              <>
                <Text style={styles.title}>{t('dispute.withdraw_title')}</Text>
                <View style={styles.bullets}>
                  <Bullet colors={colors} styles={styles} icon="flash-outline" text={t('dispute.withdraw_unfreeze')} />
                  <Bullet colors={colors} styles={styles} icon="warning-outline" text={t('dispute.withdraw_penalty')} color={colors.warning} />
                </View>
                <ErrorText>{error}</ErrorText>
                <View style={styles.btnRow}>
                  <Pressable style={styles.btnSecondary} onPress={() => setStep('reveal')}>
                    <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.btnPrimary, busy && styles.btnDisabled]}
                    onPress={doWithdraw}
                    disabled={busy}
                  >
                    {busy
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('common.confirm')}</Text>
                    }
                  </Pressable>
                </View>
              </>
            )}

            {step === 'persist_confirm' && (
              <>
                <Text style={styles.title}>{t('dispute.persist_title')}</Text>
                <View style={styles.bullets}>
                  <Bullet colors={colors} styles={styles} icon="star-half-outline" text={t('dispute.persist_loss', { article: genderedArticle(otherName), name: otherName })} color={colors.danger} />
                  <Bullet colors={colors} styles={styles} icon="close-circle-outline" text={t('dispute.persist_cancel')} color={colors.danger} />
                  <Bullet colors={colors} styles={styles} icon="document-text-outline" text={t('dispute.persist_record')} />
                </View>
                <ErrorText>{error}</ErrorText>
                <View style={styles.btnRow}>
                  <Pressable style={styles.btnSecondary} onPress={() => setStep('reveal')}>
                    <Text style={styles.btnSecondaryText}>{t('common.back')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.btnDanger, busy && styles.btnDisabled]}
                    onPress={doPersist}
                    disabled={busy}
                  >
                    {busy
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.btnPrimaryText}>{t('dispute.action_persistFinal')}</Text>
                    }
                  </Pressable>
                </View>
              </>
            )}
          </View>
        </ScrollView>
      </View>
      </KeyboardAvoidingView>
    </View>
  );
}

// ── Απόκριση στην πρόταση αμοιβαίας επίλυσης (από τον Β) ─────────────────────

type RespondMutualProps = {
  dispute: DisputeInfo;
  initiatorName: string;
  onDone: () => void;
  onCancel: () => void;
};

export function RespondMutualResolutionFlow({ dispute, initiatorName, onDone, onCancel }: RespondMutualProps) {
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deadline = dispute.mutual_resolution_expires_at
    ? new Date(dispute.mutual_resolution_expires_at).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';

  async function respond(accept: boolean) {
    setBusy(true); setError(null);
    try { await respondMutualResolution(dispute.id, accept); onDone(); }
    catch (e) { setError(getErrorMessage(e, t('dispute.errorGeneric'))); }
    finally { setBusy(false); }
  }

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onCancel}>
      <View style={[styles.overlay, { justifyContent: 'flex-end' }]}>
        <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]}>
          <View style={styles.handle} />
          <View style={styles.iconRow}>
            <Ionicons name="people" size={32} color={colors.primary} />
          </View>
          <Text style={styles.title}>{t('dispute.respondMutual_title')}</Text>
          <Text style={styles.body}>
            {t('dispute.respondMutual_body', { article: genderedArticle(initiatorName), name: initiatorName })}
          </Text>
          <View style={styles.bullets}>
            <Bullet colors={colors} styles={styles} icon="checkmark-circle-outline" text={t('dispute.respondMutual_accept')} color={colors.success} />
            <Bullet colors={colors} styles={styles} icon="close-circle-outline" text={t('dispute.respondMutual_reject')} />
            <Bullet colors={colors} styles={styles} icon="time-outline" text={t('dispute.respondMutual_deadline', { date: deadline })} color={colors.warning} />
          </View>
          <ErrorText>{error}</ErrorText>
          <Pressable
            style={[styles.btnPrimary, busy && styles.btnDisabled]}
            onPress={() => respond(true)}
            disabled={busy}
          >
            {busy
              ? <ActivityIndicator color="#fff" size="small" />
              : <Text style={styles.btnPrimaryText}>{t('dispute.action_acceptMutual')}</Text>
            }
          </Pressable>
          <Pressable
            style={[styles.btnSecondary, busy && styles.btnDisabled]}
            onPress={() => respond(false)}
            disabled={busy}
          >
            <Text style={styles.btnSecondaryText}>{t('dispute.action_rejectMutual')}</Text>
          </Pressable>
          <Pressable style={[styles.btnSecondary, { opacity: 0.5 }]} onPress={onCancel}>
            <Text style={styles.btnSecondaryText}>{t('dispute.action_decideLater')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type BulletProps = {
  icon: keyof typeof Ionicons.glyphMap;
  text: string;
  color?: string;
  colors: AppColors;
  styles: ReturnType<typeof makeStyles>;
};

function Bullet({ icon, text, color, colors, styles }: BulletProps) {
  return (
    <View style={styles.bullet}>
      <Ionicons name={icon} size={16} color={color ?? colors.primary} style={styles.bulletIcon} />
      <Text style={[styles.bulletText, color ? { color } : null]}>{text}</Text>
    </View>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.md,
    },
    wizardHeader: { gap: 6, marginBottom: spacing.xs },
    wizardStep: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
    wizardTrack: { height: 3, backgroundColor: colors.border, borderRadius: 2, overflow: 'hidden' },
    wizardFill: { height: '100%', backgroundColor: colors.primary, borderRadius: 2 },
    noteInput: {
      borderWidth: 1, borderRadius: radius.md,
      padding: spacing.md, minHeight: 100,
      fontSize: fontSize.md, lineHeight: 22,
      textAlignVertical: 'top',
    },
    handle: {
      width: 40, height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      alignSelf: 'center',
      marginBottom: spacing.xs,
    },
    iconRow: {
      alignItems: 'center',
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
    },
    body: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    noteLabel: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    noteText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    bullets: {
      gap: spacing.sm,
      paddingVertical: spacing.xs,
    },
    bullet: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
    },
    bulletIcon: {
      marginTop: 2,
    },
    bulletText: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    tagGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
    tag: {
      borderRadius: radius.full,
      borderWidth: 1.5,
      borderColor: colors.border,
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
      backgroundColor: colors.surfaceAlt,
    },
    tagSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    tagText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    tagTextSelected: {
      color: colors.textOnPrimary,
    },
    tagReadOnly: {
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.primary,
      paddingVertical: 6,
      paddingHorizontal: spacing.md,
    },
    tagReadOnlyOther: {
      borderColor: colors.warning,
    },
    tagReadOnlyText: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    revealBlock: {
      gap: spacing.sm,
    },
    evidenceLabel: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', marginTop: spacing.sm },
    evidenceGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
    evidenceThumbWrap: {
      position: 'relative',
    },
    evidenceThumb: {
      width: 72,
      height: 72,
      borderRadius: radius.sm,
      backgroundColor: colors.surfaceAlt,
    },
    evidenceThumbLoading: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    evidenceRemoveBtn: {
      position: 'absolute',
      top: -6,
      right: -6,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: colors.danger,
      alignItems: 'center',
      justifyContent: 'center',
    },
    revealWho: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    btnRow: {
      flexDirection: 'row',
      gap: spacing.sm,
    },
    btnPrimary: {
      flex: 1,
      height: 48,
      backgroundColor: colors.primary,
      borderRadius: radius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    btnDanger: {
      flex: 1,
      height: 48,
      backgroundColor: colors.danger,
      borderRadius: radius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    btnWarning: {
      height: 48,
      backgroundColor: colors.warning,
      borderRadius: radius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    btnSecondary: {
      flex: 1,
      height: 48,
      borderRadius: radius.md,
      borderWidth: 1.5,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    btnPrimaryText: {
      color: '#fff',
      fontSize: fontSize.md,
      fontWeight: '700',
      textAlign: 'center',
    },
    btnSecondaryText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    btnDisabled: { opacity: 0.5 },
    btnHint: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      textAlign: 'center',
      marginTop: -spacing.xs,
    },
    btnCommunication: {
      height: 48,
      backgroundColor: '#10B981',
      borderRadius: radius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    commDeclaredBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      backgroundColor: 'rgba(16,185,129,0.12)',
      borderRadius: radius.md,
      paddingVertical: 10,
      paddingHorizontal: spacing.md,
    },
    commDeclaredText: {
      color: '#10B981',
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
      textAlign: 'center',
    },
  });
}
